import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";

const NAV = [
    { label: "System", href: "/#features" },
    { label: "Industries", href: "/#industries" },
    { label: "Specs", href: "/#specifications" },
    { label: "Quote", href: "/#pricing" },
    { label: "Manual", href: "/installation-manual" },
    { label: "FAQ", href: "/#faq" },
];

function Logo() {
    return (
        <Link to="/" data-testid="logo-link" className="flex items-center gap-3 group">
            <div className="bg-white rounded-md flex items-center overflow-hidden group-hover:bg-white/95 transition-colors">
                <img
                    src="https://customer-assets.emergentagent.com/job_safekitchen-us/artifacts/sd8yhj0j_Content-Creator-Project.jpg"
                    alt="Kitchensafe USA"
                    className="h-12 md:h-14 w-auto block"
                />
            </div>
            <div className="hidden sm:block leading-none border-l border-white/15 pl-3">
                <div className="font-mono-tech text-[11px] text-[#a1a1aa] uppercase tracking-[0.18em]">
                    USA
                </div>
                <div className="font-mono-tech text-[11px] text-[#E53935] uppercase tracking-[0.18em] mt-1">
                    UL300a
                </div>
            </div>
        </Link>
    );
}

function DesktopNav() {
    return (
        <nav className="hidden lg:flex items-center gap-1">
            {NAV.map((n) => {
                const isRoute = n.href.startsWith("/") && !n.href.includes("#");
                if (isRoute) {
                    return (
                        <Link
                            key={n.href}
                            to={n.href}
                            data-testid={`nav-${n.label.toLowerCase()}`}
                            className="px-4 py-2 text-sm text-[#a1a1aa] hover:text-white transition-colors"
                        >
                            {n.label}
                        </Link>
                    );
                }
                return (
                    <a
                        key={n.href}
                        href={n.href}
                        data-testid={`nav-${n.label.toLowerCase()}`}
                        className="px-4 py-2 text-sm text-[#a1a1aa] hover:text-white transition-colors"
                    >
                        {n.label}
                    </a>
                );
            })}
        </nav>
    );
}

function HeaderActions({ open, setOpen }) {
    return (
        <div className="flex items-center gap-3">
            <a
                href="tel:4806945234"
                data-testid="header-phone"
                className="hidden md:flex items-center gap-2 px-4 py-2 text-sm font-medium text-white border border-white/15 hover:border-white/30 hover:bg-white/5 rounded-sm transition-colors"
            >
                <Phone className="w-3.5 h-3.5" />
                480-694-5234
            </a>
            <a
                href="/#contact"
                data-testid="header-cta"
                className="hidden sm:inline-flex items-center gap-2 px-4 py-2 text-sm font-bold bg-[#E53935] hover:bg-[#F44336] text-white rounded-sm transition-colors"
            >
                Get a Quote
            </a>
            <button
                data-testid="mobile-menu-toggle"
                onClick={() => setOpen(!open)}
                className="lg:hidden p-2 text-white"
                aria-label="Menu"
            >
                {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
        </div>
    );
}

function MobileMenu({ onClose }) {
    return (
        <div data-testid="mobile-menu" className="lg:hidden border-t border-white/10 bg-[#0a0a0b]/95 backdrop-blur-xl">
            <div className="px-6 py-4 flex flex-col gap-1">
                {NAV.map((n) => {
                    const isRoute = n.href.startsWith("/") && !n.href.includes("#");
                    if (isRoute) {
                        return (
                            <Link
                                key={n.href}
                                to={n.href}
                                onClick={onClose}
                                data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                                className="px-3 py-3 text-sm text-[#a1a1aa] hover:text-white border-b border-white/5"
                            >
                                {n.label}
                            </Link>
                        );
                    }
                    return (
                        <a
                            key={n.href}
                            href={n.href}
                            onClick={onClose}
                            data-testid={`mobile-nav-${n.label.toLowerCase()}`}
                            className="px-3 py-3 text-sm text-[#a1a1aa] hover:text-white border-b border-white/5"
                        >
                            {n.label}
                        </a>
                    );
                })}
                <a href="tel:4806945234" className="mt-2 px-3 py-3 text-sm font-medium text-[#E53935]">
                    Call Tom · 480-694-5234
                </a>
            </div>
        </div>
    );
}

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const handler = () => setScrolled(window.scrollY > 24);
        handler();
        window.addEventListener("scroll", handler, { passive: true });
        return () => window.removeEventListener("scroll", handler);
    }, []);

    // Close mobile menu on route change
    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "backdrop-blur-xl bg-[#0a0a0b]/85 border-b border-white/10" : "bg-transparent"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-16 h-20 md:h-28 flex items-center justify-between">
                <Logo />
                <DesktopNav />
                <HeaderActions open={open} setOpen={setOpen} />
            </div>
            {open && <MobileMenu onClose={() => setOpen(false)} />}
        </header>
    );
}
